import { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { removeBackground as imglyRemoveBackground, Config } from "@imgly/background-removal";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Icon from "@/components/Icon";
import Progress from "@/components/Progress";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useWindowSize } from "@/hooks/useWindowSize";
import CropModal from "@/pages/photobooth/_components/CropModal";
import AvatarPreviewCard from "@/pages/photobooth/_components/AvatarPreviewCard";
import MembershipCard, { mapMembershipCardDataToLayout } from "@/components/MembershipCard";
import { usePhotoboothUpload } from "@/pages/photobooth/usePhotoboothUpload";
import { PhotoboothPerson } from "@/pages/photobooth/queries";
import type { PhotoboothAnalyticsSource, PhotoboothCaptureMethod } from "@/pages/photobooth/_utils/amplitude";

export interface PreviewContentRef {
  save: () => Promise<void>;
  isUploading: boolean;
  hasUnsavedEdits: boolean;
}

interface ImageInfo {
  width: number;
  height: number;
  fileSizeKB: number;
  format: string;
  ratio: string;
}

function getImageFormat(dataUrl: string): string {
  const match = dataUrl.match(/^data:image\/(\w+);/);
  return match ? match[1].toUpperCase() : "Unknown";
}

function calculateRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${width / divisor}:${height / divisor}`;
}

function getBase64Size(dataUrl: string): number {
  const base64 = dataUrl.split(",")[1];
  if (!base64) return 0;
  const padding = (base64.match(/=/g) || []).length;
  return Math.round((base64.length * 0.75 - padding) / 1024);
}

interface PreviewContentProps {
  person: PhotoboothPerson;
  imageData: string;
  onRetake: () => void;
  onSaveComplete: () => void;
  analyticsSource: PhotoboothAnalyticsSource;
  captureMethod?: PhotoboothCaptureMethod;
  showFormatPreviews?: boolean;
  showDownload?: boolean;
  showInfo?: boolean;
  showActionButtons?: boolean;
  layout?: "stacked" | "side-by-side" | "responsive";
}

type BackgroundRemovalState = "idle" | "processing" | "complete" | "error";

interface HistoryEntry {
  imageData: string;
  bgRemoved: boolean;
  processedImageData: string | null;
}

const PreviewContent = forwardRef<PreviewContentRef, PreviewContentProps>(function PreviewContent(
  {
    person,
    imageData,
    onRetake,
    onSaveComplete,
    analyticsSource,
    captureMethod,
    showFormatPreviews = true,
    showDownload = true,
    showInfo = true,
    showActionButtons = true,
    layout = "stacked"
  },
  ref
) {
  const [history, setHistory] = useState<HistoryEntry[]>([{ imageData, bgRemoved: false, processedImageData: null }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const currentState = history[historyIndex];
  const currentImageData = currentState.imageData;

  const [bgRemoved, setBgRemoved] = useState(false);
  const [processedImageData, setProcessedImageData] = useState<string | null>(null);
  const [bgRemovalState, setBgRemovalState] = useState<BackgroundRemovalState>("idle");
  const [showCropModal, setShowCropModal] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [formatsExpanded, setFormatsExpanded] = useState(false);

  const { upload, isUploading, error } = usePhotoboothUpload({
    personId: person.id,
    personPgaId: person.pgaId,
    analyticsSource,
    captureMethod,
    onSuccess: onSaveComplete
  });

  const displayImage = bgRemoved && processedImageData ? processedImageData : currentImageData;
  const { isCompactLayout } = useIsCompactLayout();
  const { height: viewportHeight } = useWindowSize();
  const isResponsiveSideBySide = layout === "responsive" && !isCompactLayout;
  const isSideBySideLayout = layout === "side-by-side" || isResponsiveSideBySide;
  const showCollapsibleFormatPreviews = layout === "stacked" || (layout === "responsive" && isCompactLayout);
  const showExpandedFormatPreviews = isSideBySideLayout || !showCollapsibleFormatPreviews || formatsExpanded;
  const hasUnsavedEdits = history.length > 1 || bgRemovalState === "complete";
  const photoViewportHeight = Math.round(
    Math.min(
      isSideBySideLayout ? 720 : 520,
      Math.max(isSideBySideLayout ? 340 : 240, viewportHeight * (isSideBySideLayout ? 0.6 : 0.42))
    )
  );

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageInfo({
        width: img.naturalWidth,
        height: img.naturalHeight,
        fileSizeKB: getBase64Size(displayImage),
        format: getImageFormat(displayImage),
        ratio: calculateRatio(img.naturalWidth, img.naturalHeight)
      });
    };
    img.src = displayImage;
  }, [displayImage]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const prevState = history[newIndex];
    setBgRemoved(prevState.bgRemoved);
    setProcessedImageData(prevState.processedImageData);
    setBgRemovalState(prevState.processedImageData ? "complete" : "idle");
  }, [canUndo, historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const nextState = history[newIndex];
    setBgRemoved(nextState.bgRemoved);
    setProcessedImageData(nextState.processedImageData);
    setBgRemovalState(nextState.processedImageData ? "complete" : "idle");
  }, [canRedo, historyIndex, history]);

  const processBackgroundRemoval = useCallback(async () => {
    if (bgRemovalState === "processing") return;

    setBgRemovalState("processing");

    try {
      const config: Config = {
        model: "isnet",
        output: {
          format: "image/png",
          quality: 0.9
        }
      };

      const response = await fetch(currentImageData);
      const blob = await response.blob();
      const resultBlob = await imglyRemoveBackground(blob, config);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setProcessedImageData(base64data);
        setBgRemoved(true);
        setBgRemovalState("complete");

        setHistory((prev) => {
          const updated = [...prev];
          updated[historyIndex] = {
            ...updated[historyIndex],
            bgRemoved: true,
            processedImageData: base64data
          };
          return updated;
        });
      };
      reader.onerror = () => {
        throw new Error("Failed to read processed image");
      };
      reader.readAsDataURL(resultBlob);
    } catch {
      setBgRemovalState("error");
    }
  }, [currentImageData, bgRemovalState, historyIndex]);

  const handleBackgroundRemoval = () => {
    if (bgRemovalState === "complete" && processedImageData) {
      setBgRemoved(!bgRemoved);
    } else if (bgRemovalState === "idle" || bgRemovalState === "error") {
      processBackgroundRemoval();
    }
  };

  const handleCropComplete = (croppedImageData: string) => {
    const wasBgRemoved = !!(bgRemoved && processedImageData);

    const newEntry: HistoryEntry = {
      imageData: croppedImageData,
      bgRemoved: wasBgRemoved,
      processedImageData: wasBgRemoved ? croppedImageData : null
    };

    setHistory((prev) => [...prev.slice(0, historyIndex + 1), newEntry]);
    setHistoryIndex((prev) => prev + 1);

    if (wasBgRemoved) {
      setBgRemoved(true);
      setProcessedImageData(croppedImageData);
      setBgRemovalState("complete");
    } else {
      setBgRemoved(false);
      setProcessedImageData(null);
      setBgRemovalState("idle");
    }

    setShowCropModal(false);
  };

  const handleSave = useCallback(async () => {
    await upload(displayImage);
  }, [upload, displayImage]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      isUploading,
      hasUnsavedEdits
    }),
    [handleSave, hasUnsavedEdits, isUploading]
  );

  const downloadImage = useCallback(
    (dataUrl: string, suffix: string) => {
      const format = getImageFormat(dataUrl).toLowerCase();
      const extension = format === "png" ? "png" : "jpg";
      const filename = `${person.pgaId}_${suffix}.${extension}`;

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowDownloadMenu(false);
    },
    [person.pgaId]
  );

  const handleDownloadEdited = useCallback(() => {
    downloadImage(displayImage, "edited");
  }, [displayImage, downloadImage]);

  const handleDownloadOriginal = useCallback(() => {
    downloadImage(imageData, "original");
  }, [imageData, downloadImage]);

  const renderPhotoArea = () => (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="relative" style={{ height: `${photoViewportHeight}px` }}>
        <div
          className={`rounded-lg overflow-hidden ${
            bgRemoved && processedImageData
              ? "bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlN2ViIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNlNWU3ZWIiLz48L3N2Zz4=')]"
              : "bg-sys-brand-surface-container"
          } flex h-full w-full items-center justify-center`}
        >
          <img
            src={displayImage}
            alt="Preview"
            className="max-h-full max-w-full object-contain"
            data-testid="preview-image"
          />
          {bgRemovalState === "processing" && (
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center rounded-lg">
              <Progress />
              <p className="text-white mt-3 text-body-medium">Removing background...</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-1 sm:gap-2 p-2 bg-sys-brand-surface-container rounded-full flex-wrap">
        <button
          onClick={handleBackgroundRemoval}
          disabled={bgRemovalState === "processing"}
          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${
            bgRemoved
              ? "bg-sys-brand-primary text-sys-brand-on-primary"
              : "hover:bg-sys-brand-surface-container-high text-sys-brand-on-surface"
          } ${bgRemovalState === "processing" ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          title={bgRemovalState === "processing" ? "Processing..." : bgRemoved ? "Show original" : "Remove background"}
          data-testid="bg-removal-button"
        >
          <Icon
            name="background_replace"
            size={20}
            className={bgRemoved ? "text-sys-brand-on-primary" : "text-sys-brand-primary"}
          />
        </button>

        <div className="w-px h-6 bg-sys-brand-outline-variant" />

        <button
          onClick={() => setShowCropModal(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-sys-brand-surface-container-high transition-colors cursor-pointer"
          title="Crop photo"
          data-testid="crop-button"
        >
          <Icon name="crop" size={20} className="text-sys-brand-on-surface" />
        </button>

        <div className="w-px h-6 bg-sys-brand-outline-variant" />

        <button
          onClick={handleUndo}
          disabled={!canUndo}
          className={`flex items-center gap-2 px-2 py-2 rounded-full transition-colors ${
            canUndo
              ? "hover:bg-sys-brand-surface-container-high cursor-pointer text-sys-brand-on-surface"
              : "opacity-40 cursor-not-allowed text-sys-brand-on-surface-variant"
          }`}
          title="Undo"
          data-testid="undo-button"
        >
          <Icon name="undo" size={20} />
        </button>

        <button
          onClick={handleRedo}
          disabled={!canRedo}
          className={`flex items-center gap-2 px-2 py-2 rounded-full transition-colors ${
            canRedo
              ? "hover:bg-sys-brand-surface-container-high cursor-pointer text-sys-brand-on-surface"
              : "opacity-40 cursor-not-allowed text-sys-brand-on-surface-variant"
          }`}
          title="Redo"
          data-testid="redo-button"
        >
          <Icon name="redo" size={20} />
        </button>

        {showInfo && (
          <>
            <div className="w-px h-6 bg-sys-brand-outline-variant" />
            <button
              onClick={() => setInfoVisible(!infoVisible)}
              className={`flex items-center gap-2 px-2 py-2 rounded-full transition-colors cursor-pointer ${
                infoVisible
                  ? "bg-sys-brand-primary text-sys-brand-on-primary"
                  : "hover:bg-sys-brand-surface-container-high text-sys-brand-on-surface"
              }`}
              title="Photo information"
              data-testid="info-button"
            >
              <Icon name="info" size={20} />
            </button>
          </>
        )}

        {showDownload && (
          <>
            <div className="w-px h-6 bg-sys-brand-outline-variant" />
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className={`flex items-center gap-2 px-2 py-2 rounded-full transition-colors cursor-pointer ${
                  showDownloadMenu
                    ? "bg-sys-brand-primary text-sys-brand-on-primary"
                    : "hover:bg-sys-brand-surface-container-high text-sys-brand-on-surface"
                }`}
                title="Download photo"
                data-testid="download-button"
              >
                <Icon name="download" size={20} />
              </button>

              {showDownloadMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowDownloadMenu(false)} />
                  <div className="absolute right-0 bottom-full mb-2 bg-sys-brand-surface-container rounded-lg shadow-lg border border-sys-brand-outline-variant z-20 min-w-[200px]">
                    <button
                      onClick={handleDownloadEdited}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-sys-brand-surface-container-high transition-colors text-left rounded-t-lg"
                    >
                      <Icon name="edit" size={20} className="text-sys-brand-primary" />
                      <div>
                        <p className="text-body-medium text-sys-brand-on-surface">Download Edited</p>
                        <p className="text-body-small text-sys-brand-on-surface-variant">{person.pgaId}_edited</p>
                      </div>
                    </button>
                    <div className="h-px bg-sys-brand-outline-variant" />
                    <button
                      onClick={handleDownloadOriginal}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-sys-brand-surface-container-high transition-colors text-left rounded-b-lg"
                    >
                      <Icon name="image" size={20} className="text-sys-brand-on-surface-variant" />
                      <div>
                        <p className="text-body-medium text-sys-brand-on-surface">Download Original</p>
                        <p className="text-body-small text-sys-brand-on-surface-variant">{person.pgaId}_original</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showInfo && infoVisible && imageInfo && (
        <div className="p-4 bg-sys-brand-surface-container rounded-lg">
          <h3 className="text-label-medium font-medium text-sys-brand-on-surface mb-3">Photo Information</h3>
          <div className="grid grid-cols-2 gap-3 text-body-small">
            <div className="flex items-center gap-2">
              <Icon name="aspect_ratio" size={16} className="text-sys-brand-primary" />
              <span className="text-sys-brand-on-surface-variant">Dimensions:</span>
              <span className="text-sys-brand-on-surface font-medium">
                {imageInfo.width} × {imageInfo.height}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="data_usage" size={16} className="text-sys-brand-primary" />
              <span className="text-sys-brand-on-surface-variant">File Size:</span>
              <span className="text-sys-brand-on-surface font-medium">{imageInfo.fileSizeKB} KB</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="image" size={16} className="text-sys-brand-primary" />
              <span className="text-sys-brand-on-surface-variant">Format:</span>
              <span className="text-sys-brand-on-surface font-medium">{imageInfo.format}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="crop" size={16} className="text-sys-brand-primary" />
              <span className="text-sys-brand-on-surface-variant">Ratio:</span>
              <span className="text-sys-brand-on-surface font-medium">{imageInfo.ratio}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderFormatPreviews = () => (
    <div>
      <button
        onClick={() => setFormatsExpanded(!formatsExpanded)}
        className={`w-full flex items-center justify-between mb-3 ${isSideBySideLayout ? "cursor-default" : ""}`}
      >
        <span className="text-label-small text-sys-brand-on-surface-variant font-medium tracking-wider uppercase">
          FORMAT PREVIEWS
        </span>
        {showCollapsibleFormatPreviews && (
          <span className="flex items-center gap-1 text-sys-brand-primary text-label-medium">
            {formatsExpanded ? "Collapse" : "Expand"}
            <Icon name={formatsExpanded ? "expand_less" : "expand_more"} size={20} className="text-sys-brand-primary" />
          </span>
        )}
      </button>

      <div
        className={`flex flex-col gap-3 transition-all duration-300 ease-in-out overflow-hidden ${
          showExpandedFormatPreviews ? "max-h-none opacity-100" : "max-h-[170px] opacity-100"
        }`}
      >
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-4 sm:gap-x-6">
          <AvatarPreviewCard imageUrl={displayImage} variant="circle" />
          <AvatarPreviewCard imageUrl={displayImage} variant="rounded" />
          <AvatarPreviewCard imageUrl={displayImage} variant="standard" />
        </div>
        <div className="mt-3 flex w-full justify-center sm:mt-4">
          <div className="w-full max-w-[420px] space-y-3">
            <div className="text-center">
              <p className="text-body-medium font-semibold text-sys-brand-on-surface">Membership Card Preview</p>
              <p className="text-body-small text-sys-brand-on-surface-variant">
                Example only. The final card issued to this person may differ.
              </p>
            </div>
            <MembershipCard
              className="w-full max-w-[420px]"
              data={mapMembershipCardDataToLayout(
                {
                  orgName: "PROFESSIONAL GOLFERS' ASSOCIATION OF AMERICA",
                  memberName: `${person.firstName} ${person.lastName}, PGA`,
                  membershipType: "Reserve",
                  membershipSubtitle: "Quarter Century Member",
                  electedDate: "04/1997",
                  memberId: person.pgaId,
                  sectionName: "North Florida Section",
                  validityLabel: "Member for the Year Ending June 30",
                  validityYear: String(new Date().getFullYear()),
                  logoSrc: "/pga-logo.svg",
                  logoAlt: "PGA of America logo",
                  officers: [
                    { name: "John Lindert, PGA", title: "President" },
                    { name: "Don Rea Jr., PGA", title: "Vice President" },
                    { name: "Nathan Charnes, PGA", title: "Secretary" },
                    { name: "Seth Waugh", title: "Chief Executive Officer" }
                  ],
                  memberBadgeLabel: "Member"
                },
                displayImage,
                `${person.firstName} ${person.lastName} headshot`
              )}
            />
          </div>
        </div>
      </div>

      {showCollapsibleFormatPreviews && !formatsExpanded && (
        <div className="relative -mt-6 h-6 bg-gradient-to-t from-sys-brand-surface to-transparent pointer-events-none" />
      )}
    </div>
  );

  const renderActionButtons = () => (
    <div className="flex gap-3">
      <Button variant="outlined" onClick={onRetake} icon="refresh" testId="retake-button">
        Retake
      </Button>
      <Button
        variant="filled"
        icon={isUploading ? undefined : "check"}
        onClick={handleSave}
        disabled={isUploading}
        testId="save-button"
      >
        {isUploading ? (
          <span className="flex items-center gap-2">
            <Progress />
            Saving...
          </span>
        ) : (
          "Save to Profile"
        )}
      </Button>
    </div>
  );

  const getLayoutContent = () => {
    if (layout === "side-by-side") {
      return (
        <div className="flex min-h-0 gap-6 xl:gap-8">
          <div className="flex-1 min-w-0">{renderPhotoArea()}</div>
          {showFormatPreviews && <div className="w-[360px] xl:w-[420px] flex-shrink-0">{renderFormatPreviews()}</div>}
        </div>
      );
    }

    if (layout === "responsive") {
      if (!isResponsiveSideBySide) {
        return (
          <div className="flex min-h-0 flex-col gap-4">
            <div className="flex-1 min-w-0">{renderPhotoArea()}</div>
            {showFormatPreviews && <div className="w-full">{renderFormatPreviews()}</div>}
            {showActionButtons && renderActionButtons()}
          </div>
        );
      }

      return (
        <div className="flex min-h-0 gap-6 xl:gap-8">
          <div className="flex-1 min-w-0">{renderPhotoArea()}</div>
          {showFormatPreviews && <div className="w-[360px] xl:w-[420px] flex-shrink-0">{renderFormatPreviews()}</div>}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        {renderPhotoArea()}
        {showFormatPreviews && renderFormatPreviews()}
        {showActionButtons && renderActionButtons()}
      </div>
    );
  };

  return (
    <div className={layout === "responsive" ? "min-h-0" : undefined} data-testid="preview-content">
      {error && (
        <div className="mb-4">
          <Banner inline variant="error" description="The photo could not be saved." subText={error.message} />
        </div>
      )}

      {getLayoutContent()}

      {showCropModal && (
        <CropModal
          imageData={displayImage}
          onCrop={handleCropComplete}
          onCancel={() => setShowCropModal(false)}
          hasTransparentBackground={bgRemoved && !!processedImageData}
        />
      )}
    </div>
  );
});

export default PreviewContent;
